import axios, { AxiosInstance } from 'axios'

const BASE_URL = '/api'

interface LoginResponse {
  access_token: string
  token_type: string
  expires_in: number
  user: User
}

interface User {
  id: number
  username: string
  email: string
  full_name?: string
  is_active: boolean
  is_superuser: boolean
  can_manage_collections: boolean
  can_view_analytics: boolean
  can_export_data: boolean
  created_at: string
  last_login?: string
}

interface MarketCollection {
  id: number
  name: string
  description?: string
  is_active: boolean
  created_at: string
  card_count: number
}

interface MonitoredCard {
  id: number
  collection_id: number
  card_id: number
  ebay_search_term: string
  ebay_category_id?: string
  is_active: boolean
  created_at: string
  card_name: string
  card_set?: string
}

interface DashboardData {
  summary: {
    total_collections: number
    total_monitored_cards: number
    todays_analytics: number
    recent_volume: number
  }
  recent_logs: Array<{
    id: number
    collection_type: string
    status: string
    started_at: string
    completed_at?: string
    records_processed: number
    errors_count: number
  }>
}

interface BrowseApiStatus {
  configuration: {
    is_configured: boolean
    sandbox_mode: boolean
    daily_rate_limit: number
    requests_per_second: number
  }
  rate_limiting: {
    calls_made: number
    calls_remaining: number
    daily_limit: number
    usage_percentage: number
    errors_today: number
    rate_limited: boolean
  }
  analytics: {
    period_days: number
    total_calls: number
    total_errors: number
    total_rate_hits: number
    avg_daily_calls: number
    error_rate_percentage: number
    daily_usage: Array<{
      date: string
      calls_made: number
      errors: number
    }>
  }
  today_usage: any
}

interface MarketTrend {
  id: number
  monitored_card_id: number
  trend_date: string
  trend_direction: 'up' | 'down' | 'stable'
  price_change_pct: number
  volume_change_pct: number
  confidence_score: number
  trend_strength: 'weak' | 'moderate' | 'strong'
  supporting_indicators: Record<string, any>
  created_at: string
}

interface PriceAlert {
  id: number
  monitored_card_id: number
  alert_type: string
  threshold_price: number
  condition_operator: string
  is_active: boolean
  trigger_count: number
  notification_email?: string
  webhook_url?: string
  created_at: string
  updated_at: string
}

class ApiClient {
  private client: AxiosInstance
  private authToken: string | null = null

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      timeout: 30000, // Increased timeout for debugging
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.client.interceptors.request.use((config) => {
      console.log('Making request to:', config.url, 'with base URL:', config.baseURL)
      console.log('Full URL:', BASE_URL + config.url)
      console.log('Headers:', config.headers)
      if (this.authToken) {
        config.headers.Authorization = `Bearer ${this.authToken}`
      }
      return config
    })

    this.client.interceptors.response.use(
      (response) => {
        console.log('Response received:', response.status, response.statusText)
        return response
      },
      (error) => {
        console.error('Request failed:', error.message)
        console.error('Error details:', error.code, error.config?.url)
        if (error.response?.status === 401) {
          this.setAuthToken(null)
          localStorage.removeItem('admin_token')
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    )
  }

  setAuthToken(token: string | null) {
    this.authToken = token
  }

  // Authentication
  async login(username: string, password: string): Promise<LoginResponse> {
    const url = `/admin/auth/login`
    console.log('Making request to:', url, 'with base URL:', BASE_URL)
    console.log('Full URL will be:', BASE_URL + url)
    
    try {
      console.log('Starting login request...')
      const response = await this.client.post(url, { username, password })
      console.log('Login successful, response:', response.status)
      return response.data
    } catch (error: any) {
      console.error('Login request failed:', error)
      if (error.code === 'ECONNABORTED') {
        console.error('Request timed out - check network connectivity')
      }
      throw error
    }
  }

  async logout(): Promise<void> {
    await this.client.post('/admin/auth/logout')
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get('/admin/auth/me')
    return response.data
  }

  // Dashboard
  async getDashboardData(): Promise<DashboardData> {
    const response = await this.client.get('/admin/dashboard')
    return response.data
  }

  // Analytics
  async getCardAnalytics(monitoredCardId: number, days: number = 30) {
    const response = await this.client.get(
      `/admin/cards/${monitoredCardId}/analytics?days=${days}`
    )
    return response.data
  }

  // Data Collection
  async triggerDailyAnalytics(collectionId?: number) {
    const response = await this.client.post('/admin/data-collection/daily-analytics', {
      collection_id: collectionId,
    })
    return response.data
  }

  async triggerListingSync(monitoredCardId?: number) {
    const response = await this.client.post('/admin/data-collection/sync-listings', {
      monitored_card_id: monitoredCardId,
    })
    return response.data
  }

  // System
  async getSystemStatus() {
    const response = await this.client.get('/admin/system/status')
    return response.data
  }

  // Export
  async exportCollectionData(collectionId: number, format: string = 'json', days: number = 30) {
    const response = await this.client.get(
      `/admin/export/collection/${collectionId}?format=${format}&days=${days}`
    )
    return response.data
  }

  // Cards search (from main API)
  async searchCards(search?: string, rarity?: string, limit: number = 50) {
    const params = new URLSearchParams()
    if (search) params.append('search', search)
    if (rarity) params.append('rarity', rarity)
    params.append('limit', limit.toString())

    const response = await this.client.get(`/market/cards?${params}`)
    return response.data
  }

  // Market Intelligence Methods
  async getMarketIntelligence(timeRange: number = 30) {
    const response = await this.client.get(`/admin/dashboard/comprehensive?days_back=${timeRange}&include_trends=true&include_competitors=true`)
    return response.data
  }

  async triggerDataCollection(collectionType: string = 'all', monitored_card_id?: number) {
    const params = new URLSearchParams({ collection_type: collectionType })
    if (monitored_card_id) {
      params.append('monitored_card_id', monitored_card_id.toString())
    }
    const response = await this.client.post(`/admin/data-collection/trigger?${params}`)
    return response.data
  }

  async getPerformanceAnalytics(timeRange: number = 30) {
    const response = await this.client.get(`/admin/analytics/performance?days_back=${timeRange}`)
    return response.data
  }

  async getMarketInsights(timeRange: number = 30) {
    const response = await this.client.get(`/admin/market/insights?days_back=${timeRange}`)
    return response.data
  }

  // Price Chart Data
  async getHourlyPriceData(cardId: number, hoursBack: number = 48) {
    const response = await this.client.get(`/admin/charts/hourly/${cardId}?hours_back=${hoursBack}`)
    return response.data
  }

  async getDailyPriceData(cardId: number, daysBack: number = 30) {
    const response = await this.client.get(`/admin/charts/daily/${cardId}?days_back=${daysBack}`)
    return response.data
  }

  // eBay API Monitoring Methods
  async getEbayHealthStatus() {
    const response = await this.client.get('/admin/ebay/health/status')
    return response.data
  }

  async testEbayEndpoint(testRequest: {
    api_type: 'finding' | 'shopping' | 'trading'
    endpoint: string
    params: Record<string, any>
  }) {
    const response = await this.client.post('/admin/ebay/test/endpoint', testRequest)
    return response.data
  }

  async getEbayOAuthAuthUrl(state?: string) {
    const params = state ? `?state=${encodeURIComponent(state)}` : ''
    const response = await this.client.get(`/admin/ebay/oauth/auth-url${params}`)
    return response.data
  }

  async exchangeOAuthToken(code: string) {
    const response = await this.client.post('/admin/ebay/oauth/exchange-token', { code })
    return response.data
  }

  async getOAuthStatus() {
    const response = await this.client.get('/admin/ebay/oauth/status')
    return response.data
  }

  async runEbayDiagnostics() {
    const response = await this.client.post('/admin/ebay/monitoring/run-diagnostics')
    return response.data
  }

  async getEbayMonitoringHistory() {
    const response = await this.client.get('/admin/ebay/monitoring/history')
    return response.data
  }

  // Browse API Integration Methods
  async getBrowseApiStatus() {
    const response = await this.client.get('/admin/browse-api/status')
    return response.data
  }

  async getBrowseApiUsageHistory() {
    const response = await this.client.get('/admin/browse-api/usage-history')
    return response.data
  }

  async getMarketTrends(params?: { monitored_card_id?: number; days?: number; trend_direction?: string }) {
    const response = await this.client.get('/admin/market-trends', { params })
    return response.data
  }

  async getCompetitorAnalysis(params?: { days?: number; competitor_name?: string }) {
    const response = await this.client.get('/admin/competitor-analysis', { params })
    return response.data
  }

  async getPriceAlerts(params?: { monitored_card_id?: number; is_active?: boolean }) {
    const response = await this.client.get('/admin/price-alerts', { params })
    return response.data
  }

  async createPriceAlert(alert: {
    monitored_card_id: number
    alert_type: string
    threshold_price: number
    condition_operator: string
    notification_email?: string
    webhook_url?: string
  }) {
    const response = await this.client.post('/admin/price-alerts', alert)
    return response.data
  }

  async updatePriceAlert(alertId: number, alert: {
    threshold_price?: number
    condition_operator?: string
    is_active?: boolean
    notification_email?: string
    webhook_url?: string
  }) {
    const response = await this.client.put(`/admin/price-alerts/${alertId}`, alert)
    return response.data
  }

  async getEnhancedAnalytics() {
    const response = await this.client.get('/admin/enhanced-analytics')
    return response.data
  }

  async getBrowseApiSystemHealth() {
    const response = await this.client.get('/admin/system-health')
    return response.data
  }

  // Historical data methods (these would need backend implementation)
  async getHistoricalPriceData(itemId: number, timeRange: any) {
    const response = await this.client.get(`/admin/historical-data/${itemId}`, {
      params: timeRange
    })
    return response.data
  }

  async storeHistoricalDataPoint(itemId: number, dataPoint: any) {
    const response = await this.client.post(`/admin/historical-data/${itemId}`, dataPoint)
    return response.data
  }

  async getCollectionItems(collectionId: number) {
    const response = await this.client.get(`/collections/${collectionId}/items`)
    return response.data
  }

  async getMarketTrendingBoxes() {
    const response = await this.client.get('/admin/market/trending-boxes')
    return response.data
  }

  // Enhanced Collections API methods
  async getCollectionsSummary() {
    const response = await this.client.get('/market/collections-summary')
    return response.data
  }

  async getCollectionsBoxSets(limit: number = 100, offset: number = 0, _withEbayDataOnly: boolean = false) {
    const params = {
      limit: limit,
      skip: offset,
      sort_by: 'name',
      order: 'asc'
    }
    const response = await this.client.get('/market/box-sets', { params })
    return response.data
  }

  async getCollectionsPageData(limit: number = 100, offset: number = 0) {
    const params = { limit, skip: offset }
    const response = await this.client.get('/market/box-sets', { params })
    return response.data
  }

  // Enhanced Chart Data API - Uses eBay Analytics & Selling APIs with OAuth tokens
  async getCollectionsChartData(productId: number, searchTerm: string) {
    const response = await this.client.post('/charts/enhanced-chart-data', {
      product_id: productId,
      product_name: searchTerm
    })
    return response.data
  }

  async getLiveVolumeAnalysis(productName: string, days: number = 30) {
    const response = await this.client.post('/charts/live-volume-analysis', {
      product_name: productName,
      days: days
    })
    return response.data
  }

  async testChartConnection() {
    const response = await this.client.get('/charts/test-connection')
    return response.data
  }

  async getVolumeStatus(productId: number) {
    const response = await this.client.get(`/market/volume/status/${productId}`)
    return response.data
  }

  async refreshCollectionsMarketData(productId?: number) {
    const endpoint = productId ? `/market/refresh/${productId}` : '/market/refresh'
    const response = await this.client.post(endpoint)
    return response.data
  }
}

export const apiClient = new ApiClient()
export type { User, MarketCollection, MonitoredCard, DashboardData, BrowseApiStatus, MarketTrend, PriceAlert }
